import {
  DEFAULT_DOCTRINE_ON_TIMEOUT,
  DOCTRINE_PICK_WINDOW_DURATION_SEC,
  DOCTRINE_PICK_WINDOW_OPEN_SEC,
} from '../../data/doctrines.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import { tickOf } from '../ids.js';

// V1.5 DoctrineSystem owns the pick-window lifecycle (docs/02_GAME_DESIGN.md §6).
//
// Lifecycle, observed each tick after captureSystem (so the lifecycle reflects the
// post-tick world) and before economySystem (so a doctrine picked this tick takes
// effect at the next tick boundary uniformly across modifiers):
//
//   1. matchSeconds reaches DOCTRINE_PICK_WINDOW_OPEN_SEC (3:00):
//        doctrinePickWindowOpen flips true; closesAtTick is set.
//   2. matchSeconds reaches DOCTRINE_PICK_WINDOW_OPEN_SEC + DURATION (3:15):
//        doctrinePickWindowOpen flips false; any player whose doctrineId is still
//        null is auto-assigned DEFAULT_DOCTRINE_ON_TIMEOUT (vanguard).
//
// The window opens exactly once per match; the close path runs exactly once per
// match. The host detects the open/close transitions via boolean diffs and emits
// the lifecycle events once each.
//
// Why a separate system: the open/close transitions are simple but their *timing*
// (exact tick boundary at 3:00) needs to be deterministic and observable. Putting
// it in a system means tests can drive it via Simulation.tick without spinning up
// a host accumulator.

export function doctrineSystem(state: MatchState): void {
  const openSec = DOCTRINE_PICK_WINDOW_OPEN_SEC;
  const closeSec = openSec + DOCTRINE_PICK_WINDOW_DURATION_SEC;
  // Open transition: matchSeconds crosses the open threshold and neither already-
  // open nor already-closed. We detect "not yet opened" by closesAtTick === null.
  if (
    !state.doctrinePickWindowOpen &&
    state.doctrinePickWindowClosesAtTick === null &&
    state.matchSeconds >= openSec
  ) {
    const ticksToClose = Math.max(1, Math.round(DOCTRINE_PICK_WINDOW_DURATION_SEC / SECONDS_PER_TICK));
    state.doctrinePickWindowOpen = true;
    state.doctrinePickWindowClosesAtTick = tickOf(state.tick + ticksToClose);
    return;
  }
  // Close transition: window is open and matchSeconds >= openSec + duration. We
  // close on time-elapsed rather than tick-equality so a host that pumps a few
  // ticks past the close still resolves the window.
  if (state.doctrinePickWindowOpen && state.matchSeconds >= closeSec) {
    state.doctrinePickWindowOpen = false;
    for (const player of state.players) {
      if (player.doctrineId === null) player.doctrineId = DEFAULT_DOCTRINE_ON_TIMEOUT;
    }
  }
}
